<template>
  <div
    class="card-product-row"
    :data-product-id="product.id"
  >
    <div
      ref="productImg"
      class="card-product-row__media slider-images-navigation-button"
    >
      <div v-if="showSwiper">
        <swiper
          :modules="[Navigation, Pagination,Virtual]"
          :navigation="true"
          :pagination="true"
          virtual
          @swiper="setRefSwiper"
          @click="openZoomImages"
        >
          <swiper-slide
            v-for="imageId in product.imageIds"
            :key="`product-image-${product.id}-${imageId}`"
          >
            <nuxt-img
              :src="String(imageId)"
              width="420"
              height="420"
              fit="default"
              loading="lazy"
              :modifiers="{'border': 'default'}"
              :alt="`product-image-${product.id}-${imageId}`"
            />
            <div class="swiper-lazy-preloader"></div>
          </swiper-slide>
        </swiper>

        <div
          class="card-product-row__media-control --zoom"
          @click="openZoomImages"
        >
          <img
            src="~/assets/img/svg/common/search.svg"
            width="20"
            height="20"
            alt=""
          />
        </div>
        <div class="card-product-row__media-control --wish">
          <WishBtn :product-id="+product.id" />
        </div>
      </div>
    </div>

    <div class="card-product-row__content">
      <nuxt-link
        :to="product.url"
        class="card-product-row__name h3 semibold hover-decoration"
        @click.capture="clickLinkProduct"
      >
        {{ product.name }}
      </nuxt-link>
      <div
        v-if="tags.length"
        class="card-product-row__tags"
      >
        <TagItem
          v-for="(tag, index) in tags"
          :key="`TagItem-${ index }`"
          :tag="tag"
        />
      </div>
      <div class="card-product-row__characteristics product-characteristics">
        <template
          v-for="(item) in characteristics"
          :key="`characteristics-${item.id}`"
        >
          <CharacteristicsRow
            v-if="Boolean(!item?.isHide || isFullCharacteristics)"
            :label="item.nameCustom"
            :value="item.valueMessage"
            :is-full-width="Boolean(item.isFullWidth)"
            :is-values-gap="Boolean(item.isValuesGap)"
          />
        </template>
        <template v-if="characteristics.length > 7">
          <div class="--more">
            <div @click="changeFullCharacteristics">
              {{ isFullCharacteristics ? 'Скрыть все характеристики' : 'Все характеристики' }}
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="card-product-row__action">
      <template v-if="isHasOptions">
        <div class="card-product-row__options">
          <div class="--label">Выберите комплектацию:</div>
          <div class="--options">
            <template
              v-for="(option) in listOptions"
              :key="`option-${option.id}`"
            >
              <SelectOption
                :option="option"
                :product="product"
                :product-slug="product.slug"
                :value="selectedOptions[option.id]"
                :selected="selectedOptions"
                :error="selectedOptionsError[option.id]"
                @change="(variant) => changeSelectedOptions(option, variant)"
              />
            </template>
          </div>
        </div>
      </template>
      <div
        class="card-product-row__total-amount"
        :class="{'--discount': Boolean(oldPriceProduct)}"
      >
        <div class="--label">{{ pricesLabel }}</div>
        <div
          v-if="oldPriceProduct"
          class="--old-price"
        >
          {{ $numberFormat(oldPriceProduct) }} ₽
        </div>
        <div class="--current-price">
          {{ $numberFormat(priceProduct) }} ₽
        </div>
      </div>
      <div class="card-product-row__add-to-cart">
        <add-to-cart-button-with-counter
          :product="product"
          :selected-options="selectedOptions"
          :is-reset-settings="isResetAddToCartBtn"

          @errorList="checkError"
        />
      </div>
      <div class="card-product-row__more-info-product">
        <nuxt-link
          :to="product.url"
          class="hover-decoration"
          @click.capture="clickLinkProduct"
        >
          Подробнее о товаре
        </nuxt-link>
      </div>
    </div>
    <ProductCardMarking
      :product="product"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { Navigation, Pagination, Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';

import { useIntersectionObserver } from '@vueuse/core';
import SelectOption from '~/components/options/SelectOption.vue';
import getDiscountLabel from '~/utils/getDiscountLabel';
import { useCookie } from '#imports';
import {
  type IProduct,
  type ProductCharacteristic,
  type ProductCharacteristics
} from '#sitis/internal/controllers/products/models/Product';
import AddToCartButtonWithCounter from '~/components/widget/AddToCartButtonWithCounter.vue';
import { getTotalOldPriceOptions, getTotalPriceOptions } from '~/utils/productOptions';
import { scrollToElement } from '~/utils/scrollToElement';
import getPromotionsProduct from '~/utils/getPromotionsProduct';
import TagItem from '~/components/collection/card-product-row/TagItem.vue';
import CharacteristicsRow from "~/components/products/_CharacteristicsRow.vue";
import ProductCardMarking from "~/components/catalog/ProductCardMarking.vue";

const WishBtn = defineAsyncComponent(() => import('~/components/widget/WishBtn.vue'));
const cookieCollection = useCookie<{ productId: number; collectionSlug: string } | null>('cookie-scroll-collection');

const props = defineProps<{
	product: IProduct,
	isCollection: boolean,
}>();

const { $numberFormat } = useNuxtApp();
const $route = useRoute();
const { openGallery } = useGallery();

const tags = computed(() => {
  return getPromotionsProduct(props.product);
});

const isHasOptions = computed(() => Boolean(props.product?.hasOptions));
const priceProduct = computed(() => {
  const optionsPrice = getTotalPriceOptions(
    selectedOptions.value,
    props.product.price,
    listOptions.value
  );
  return props.product.price + optionsPrice;
});

const oldPriceProduct = computed(() => {
  if (!props.product.oldPrice || props.product.oldPrice <= props.product.price) {
    return null;
  }

  const optionsPrice = getTotalOldPriceOptions(
    selectedOptions.value,
    props.product.oldPrice,
    listOptions.value
  );
  return props.product.oldPrice + optionsPrice;
});

// Инстанс Swiper
const refSwiper = ref({ activeIndex: 0 });
const productImg = ref<HTMLDivElement | null>(null);
const showSwiper = ref<boolean>(false);

const setRefSwiper = (config: any) => {
  refSwiper.value = config;
};

// Логика развернутого описания
const characteristics: any = computed(() => {
  const hiddenChar = ['popular_categories', 'kommentarij-sluzhebnyj', 'discount-label-date', 'discount-label-auto', 'discount-label'];

  let _characteristics: ProductCharacteristic[] = [];
  (props?.product?.characteristics || []).forEach((item) => {
    _characteristics.push(...(item?.characteristics || []));
  });
  _characteristics = _characteristics.filter((item: ProductCharacteristic) => {
    return Boolean(!hiddenChar.includes(item.slug) && item.showInProduct);
  });
  _characteristics = _characteristics.map((item: any, index: number) => ({
    ...item,
    isHide: Boolean(index >= 7),
    isFullWidth: Boolean(item.viewTemplate === 'full_width'),
    isValuesGap: Boolean(item.viewTemplate === 'list_column'),
    nameCustom: String(item.nameForProduct || item.name || '').split(" ").map((t) => `<span>${t}</span>`).join(' '),
    valueMessage: (item?.values || []).map((t: any) => t.value)
      .filter((t: any) => !!t)
      .map((t: any) => {
        if (item.viewTemplate === 'list_column') {
          return `<span class="--tag">${t}</span>`;
        }
        return t;
      })
      .join(Boolean(item.viewTemplate === 'list_column') ? '' : ', ')
  }));
  return _characteristics;
});

const isFullCharacteristics = ref(false);
const changeFullCharacteristics = () => {
  isFullCharacteristics.value = !isFullCharacteristics.value;
};

// Логика по опциям товара
const listOptions = computed(() => props.product?.options || []);
const selectedOptions = ref<{ [key: string]: any }>({});
const selectedOptionsError = ref<{ [key: number]: string }>({});
const lastSelectedOptions = ref<{ [key: string]: any }>({});
const isResetAddToCartBtn = ref<boolean>(false);
const countOfAddedProduct = ref<number>(0);

const changeSelectedOptions = (option: any, variant: any) => {
  lastSelectedOptions.value = {
    options: option,
    variant: variant
  };

  selectedOptions.value[option.id] = variant;

  if (listOptions.value.length === Object.keys(selectedOptions.value).length && countOfAddedProduct.value > 0) {
    isResetAddToCartBtn.value = true;
  }
};

// Логика добаления товара в корзину
const checkError = (errors: { [key: number]: string }) => {
  if (countOfAddedProduct.value > 0) {
    isResetAddToCartBtn.value = false;
  }

  countOfAddedProduct.value++;
  selectedOptionsError.value = errors;
};

const openZoomImages = () => {
  const activeIndex = Number.parseFloat(String(refSwiper.value?.activeIndex || 0));
  const payload = {
    imagesIds: props.product.imageIds,
    videos: props.product.videos,
    indexActiveSlide: activeIndex
  };
  // Defer to next frame so the click paints first (better INP)
  requestAnimationFrame(() => {
    openGallery(payload);
  });
};

// Логика скрола к товары при обратном переходе
const clickLinkProduct = () => {
  if (!props.isCollection) {
    return;
  }

  const slug = ($route.path || '').split('/').pop();

  cookieCollection.value = {
    productId: +props.product.id,
    collectionSlug: slug!
  };
};
const scrollToProductInCollection = () => {
  if (!props.isCollection) {
    return;
  }

  if (!cookieCollection.value || Boolean(String(cookieCollection.value?.productId) !== String(props.product.id))) {
    return;
  }

  const slug = ($route.path || '').split('/').pop();
  if (cookieCollection.value?.collectionSlug !== slug) {
    return;
  }

  setTimeout(() => {
    scrollToElement(`[data-product-id="${props.product.id}"]`, {
      offseetTop: 120
    });
    cookieCollection.value = null;
  }, 501);
};

// Логика "от", что цена стартовая
const addedLowerLimitPricePhrases = computed(() => {
  let _findCharGroup:ProductCharacteristics | undefined = props?.product?.characteristics.find((charGroup: ProductCharacteristics) => {
    return charGroup.characteristics.find((char: ProductCharacteristic) => char.slug === 'add-to-price')
  })
  if (_findCharGroup && _findCharGroup.characteristics) {
    const _index = _findCharGroup.characteristics.findIndex((char) => char.slug === 'add-to-price')
    return _findCharGroup?.characteristics?.[_index]?.values?.[0]?.value === 'да'
  }
})
const pricesLabel = computed(() => {
  return addedLowerLimitPricePhrases.value && isHasOptions.value ? 'Цена от:' : 'Итог:'
})


onMounted(() => {
  scrollToProductInCollection();
});

const { stop } = useIntersectionObserver(
  productImg,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      showSwiper.value = isIntersecting;
      stop();
    }
  }
);
onBeforeUnmount(() => {
  stop();
});
</script>

<style lang="scss">
.card-product-row {
	display: flex;
	align-items: flex-start;
}

.card-product-row__media {
	margin-right: 64px;
	width: 100%;
	max-width: 413px;
	border-radius: 20px;
	position: relative;
	overflow: hidden;
	background-color: #F4F3F1;

	&::after {
		content: "";
		float: left;
		padding-top: 100%;
	}

	.swiper {
		position: absolute;
		inset: 0;
	}

	.swiper-slide {
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: #F4F3F1;
    cursor: pointer;
	}

	.swiper-slide img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
		mix-blend-mode: multiply;
	}

	.swiper-pagination {

	}

	.swiper-pagination-bullet {
		width: 5px;
		height: 5px;
		opacity: 0.5;
		background-color: #000;
	}

	.swiper-pagination-bullet-active {
		background-color: black;
		opacity: 1;
	}
}

.card-product-row__content {
	flex: 1;
	padding: 22px 0;
	box-sizing: border-box;
}

.card-product-row__action {
	width: 100%;
	max-width: 413px;
	margin-left: 36px;
	border-radius: 20px;
	border: 1px solid #F4F3F1;
	background: #FFF;
	padding: 24px;
	box-sizing: border-box;
}

.card-product-row__name {
	display: block;
	margin-bottom: 16px;
}

.card-product-row__tags {
	display: flex;
	flex-wrap: wrap;
	margin-top: -10px;
	margin-left: -10px;
	margin-bottom: 24px;

	& > * {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 6px 16px;
		box-sizing: border-box;
		margin-top: 10px;
		margin-left: 10px;
		border-radius: 7px;
		background: #EBEBEB;
		color: #000;
		font-size: 12px;
		line-height: 110%;
		font-weight: 500;
		position: relative;

		&.--icon {
			width: 28px;
			height: 28px;
			padding: 4px;

			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}

		&.--discount {
			font-weight: 600;
		}

		&:hover {
			z-index: 10;
		}
	}

	.tooltip__hover {
		max-width: 320px;
	}
}

.card-product-row__characteristics {
	.--more {
		display: flex;
		align-items: flex-start;
		justify-content: flex-start;
		margin-top: 16px;

		div {
			color: #333;
			font-variant-numeric: lining-nums proportional-nums;
			font-size: 14px;
			font-weight: 500;
			line-height: 20px;
			text-decoration-line: underline;
			transition: all 0.3s;
			cursor: pointer;

			&:hover {
				text-decoration-color: transparent;
			}
		}
	}
}

.card-product-row__media-control {
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	z-index: 10;
	width: 38px;
	height: 38px;
	border-radius: 10px;
	cursor: pointer;
	background-color: white;

	&.--wish {
		top: 16px;
		right: 16px;

		img {
			filter: invert(83%) sepia(53%) saturate(1392%) hue-rotate(300deg) brightness(96%) contrast(92%);
		}
	}

	&.--zoom {
		bottom: 16px;
		right: 16px;
	}

	&:hover {
		background-color: #EBEBEB;
	}
}

.card-product-row__options {
	margin-bottom: 32px;

	.--label {
		margin-bottom: 16px;
		color: #000;
		font-size: 16px;
		font-weight: 600;
		line-height: 120%;
	}

	.--options {
		display: flex;
		flex-direction: column;

		& > * {
			margin-top: 16px;

			&:first-child {
				margin-top: 0;
			}
		}
	}
}

.card-product-row__total-amount {
	display: flex;
	align-items: flex-end;
	font-size: 20px;
	font-weight: 600;
	line-height: 120%;

	.--label {
		color: #404040;
		margin-right: auto;
	}

	.--old-price {
		margin-right: 6px;
		color: #B8B8B8;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 12px;
		font-weight: 500;
		line-height: 20px;
		text-decoration: line-through;
	}

	.--current-price {
	}

	&.--discount {
		.--current-price {
			color: #ED908C;
		}
	}
}

.card-product-row__add-to-cart {
	margin-top: 16px;

	& .template-cart-btn {
		& .title > span {
			display: none;
		}
	}

	.btn {
		width: 100%;
		height: 60px;
		border-radius: 16px;
		text-align: center;
		font-weight: 600;
	}
}

.card-product-row__more-info-product {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 16px;
	color: #989898;
	font-variant-numeric: lining-nums proportional-nums;
	font-size: 12px;
	font-weight: 500;
	line-height: 20px;

	div {
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}
}

@media (max-width: 1699px) {
	.card-product-row__media {
		margin-right: 32px;
	}
}

@media (max-width: 1599px) {
	.card-product-row__media {
		max-width: 360px;
	}
	.card-product-row__action {
		max-width: 320px;
		margin-left: 20px;
	}
}

@media (max-width: 1499px) {
	.card-product-row__media {
		margin-right: 16px;
	}
	.card-product-row__action {
		max-width: 370px;
	}
	.card-product-row__characteristics {
		//.--value {
		//	max-width: 225px;
		//}
	}
}

@media (max-width: 1359px) {
	.card-product-row__media {
		max-width: 320px;
	}
	.card-product-row__content {
		padding: 16px 0;
	}
	.card-product-row__action {
		max-width: 320px;
		padding: 20px;
		border-radius: 16px;
	}
}

@media (max-width: 1199px) {
	.card-product-row__media {
		max-width: 280px;
	}
	.card-product-row__content {
		padding: 8px 0;
	}
	.card-product-row__name {
		font-size: 20px;
		line-height: 22px;
	}
	.card-product-row__action {
		max-width: 280px;
		padding: 20px;
		border-radius: 10px;
		margin-left: 15px;
	}
	.card-product-row__add-to-cart {
		.template-cart-btn .btn {
			height: 48px;
			border-radius: 8px;
		}
	}
	.card-product-row__characteristics {
		//.--value {
		//	max-width: 130px;
		//}
    //
		//.--label, .--value {
		//	font-size: 12px;
		//	line-height: 14px;
		//}
	}
}

</style>
